import dayjs from "dayjs";
import numeral from "numeral";

import React, { useCallback } from "react";

import { Box, Grid, Typography } from "@mui/material";

import usePlatform from "@/hooks/usePlatform";
import { CalendarItemType } from "@/typings";

interface CalendarItemProps {
  data: CalendarItemType;
  month: string;
  onItemClick?: (data: CalendarItemType["points"][number]) => void;
}

const PointItem = ({
  data,
  color,
  onItemClick,
}: {
  data: CalendarItemType["points"][number];
  color?: string;
  onItemClick?: (data: CalendarItemType["points"][number]) => void;
}) => {
  const { isMobile } = usePlatform();
  const available = !!data.value;

  const formatVal = useCallback(
    (val: number, unit = "") => {
      if (val >= 1000 && isMobile) {
        return `${numeral(val / 1000).format("0,0")}k ${isMobile ? "" : unit}`;
      }

      return `${numeral(val).format("0,0")} ${isMobile ? "" : unit}`;
    },
    [isMobile]
  );

  return (
    <Box
      className="mb-[2px] rounded-[3px]"
      sx={{
        backgroundColor: color || "transparent",
        opacity: available ? 1 : 0.38,
      }}
      onClick={() => (available && onItemClick ? onItemClick(data) : undefined)}
    >
      <Typography
        className="px-[2px] h-[24px] !text-[#fff] !leading-[24px] overflow-hidden text-ellipsis whitespace-nowrap"
        sx={{
          fontSize: isMobile ? "12px" : "13px",
          fontWeight: available ? "normal" : "bold",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title={available ? formatVal(data.value as number, data.unit) : ""}
      >
        {data.icon}
        {available
          ? formatVal(data.value as number, data.unit)
          : isMobile
          ? "N/A"
          : "Not Available"}
      </Typography>
    </Box>
  );
};

const CalendarItem = (props: CalendarItemProps) => {
  const {
    data: { date, showDate = true, points },
    month,
    onItemClick,
  } = props;

  const isToday = date === dayjs().format("YYYY-MM-DD");
  const isCurrentMonth = dayjs(date).format("YYYY-MM") === month;

  return (
    <Grid key={date} item xs={12 / 7} className="pb-[20px]">
      <Box
        className="flex flex-col px-[2px] h-full"
        sx={{
          backgroundColor: (theme) => (isToday ? theme.palette.action.selected : ""),
          borderRadius: "4px",
        }}
      >
        <Typography
          className="text-right !text-[15px] p-[2px]"
          sx={{
            color: (theme) =>
              isCurrentMonth ? theme.palette.text.primary : theme.palette.text.disabled,
          }}
        >
          {dayjs(date).format("D")}
        </Typography>

        {(points || []).map((ele, index) => {
          return showDate ? (
            <PointItem data={ele} key={index} color={ele.color} onItemClick={onItemClick} />
          ) : (
            <Box
              key={index}
              className="mb-[2px] rounded-[3px] px-[2px] h-[24px]"
              sx={{ backgroundColor: ele.color || "transparent", opacity: 0.38 }}
            />
          );
        })}
      </Box>
    </Grid>
  );
};

export default React.memo(CalendarItem);
